import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatFormField, MatHint, MatInput } from '@angular/material/input';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, from, map, mergeMap, of, takeUntil } from 'rxjs';
import { Institution } from '../../../api/app-types/institution.app';
import { PERSON_TYPE, SearchResultItem } from '../../../api/app-types/search.app';
import { SearchService } from '../../../api/services/search.service';
import { BaseDestroy } from '../../../core/components/base-destroy/base-destroy';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';

export interface Suggestion {
	id: number;
	name: string;
	type: 'institution' | 'branch';
	institutionId?: number;
	isMainInstitution?: boolean;
	addressText?: string;
}

@Component({
	selector: 'lvr-new-institution-dialog',
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './new-institution-dialog.html',
	styleUrl: './new-institution-dialog.scss',
	imports: [
		MatDialogModule,
		MatIconModule,
		MatInput,
		LvrInputDirective,
		MatFormField,
		LvrPaddingDirective,
		TranslocoPipe,
		ReactiveFormsModule,
		MatAutocompleteModule,
		MatHint,
		LvrButton,
	],
	providers: [
		{
			provide: SearchService,
			useClass: SearchService,
		},
	],
})
export class NewInstitutionDialog extends BaseDestroy implements OnInit {
	private readonly router = inject(Router);
	private readonly fb = inject(FormBuilder);
	private readonly searchApi = inject(SearchService);
	public readonly selfRef = inject(MatDialogRef<NewInstitutionDialog>);
	public readonly form = this.initForm();
	public readonly suggestions = signal<Suggestion[]>([]);
	private readonly institution = toSignal<Suggestion | string>(
		this.form.get('institutionName')!.valueChanges.pipe(
			debounceTime(300),
			map((v) => v || ''),
		),
	);
	public readonly showWarning = computed(() => this.suggestions().length > 0 && typeof this.institution() === 'string');
	public readonly btnText = computed(() =>
		typeof this.institution() === 'string' ? 'institution.create.create' : 'institution.create.select',
	);

	public ngOnInit() {
		this.registerFetchSuggestions();
	}

	public displayName(institution?: Institution) {
		if (!institution) return '';
		return institution.name ?? `ID: ${institution.id}`; // ID is only shown in an edge case where the institution has no name (which should never happen).
	}

	public done() {
		const selected = this.institution();
		// if user selected an existing institution, navigate to it
		if (typeof selected === 'object') {
			if (selected.type === 'institution') {
				this.router.navigate(['/desk/institution', selected.id]);
			} else {
				this.router.navigate(['/desk/institution', selected.institutionId, 'workplace', selected.id]);
			}

			this.selfRef.close();
			return;
		}
		const formValue = this.form.getRawValue();
		this.router.navigate(['/desk/institution/add'], {
			queryParams: {
				name: formValue.institutionName,
			},
		});
		this.selfRef.close();
	}

	private initForm() {
		const form = this.fb.group({
			institutionName: ['', [Validators.required]],
		});
		return form;
	}

	private registerFetchSuggestions() {
		this.form.valueChanges
			.pipe(
				distinctUntilChanged(),
				takeUntil(this.destroy$),
				debounceTime(300),
				map(() => this.form.get('institutionName')?.value ?? ''),
				mergeMap((value) => {
					if (!value || typeof value !== 'string') return of(null);

					return from(
						this.searchApi.search({
							searchInstitutions: true,
							searchInstitutionBranches: true,
							searchClientEnvironments: false,
							searchClients: false,
							searchUsers: false,
							searchInstitutionPerson: false,
							searchSponsors: false,
							searchIso: false,
							searchTerm: value,
						}),
					);
				}),
				map((result) => {
					if (!result) return [];

					const items = result.entities ?? result.entities ?? [];
					const mapped = items
						.map((entry: SearchResultItem): Suggestion | null => {
							const item = entry.item;
							const personType = entry.personType;

							if (personType === PERSON_TYPE.INSTITUTION) {
								return {
									id: item.id,
									name: item.name,
									type: 'institution',
									addressText: this.buildAddress(item.address),
								};
							}

							if (personType === PERSON_TYPE.INSTITUTION_BRANCH) {
								return {
									id: item.id,
									name: item.name,
									type: 'branch',
									institutionId: item.institutionId,
									isMainInstitution: item.isMainInstitution,
									addressText: this.buildAddress(item.address),
								};
							}

							return null;
						})
						.filter((x): x is Suggestion => x !== null);

					return mapped.filter((item) => !(item.type === 'branch' && item.isMainInstitution));
				}),
			)
			.subscribe((suggestions) => this.suggestions.set(suggestions));
	}

	private buildAddress(address?: { street?: string; housenumber?: string; postalCode?: string; location?: string }) {
		if (!address) return '';

		const streetLine = [address.street, address.housenumber].filter(Boolean).join(' ');
		const cityLine = [address.postalCode, address.location].filter(Boolean).join(' ');

		return [streetLine, cityLine].filter(Boolean).join(', ');
	}
}
